import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Company, listCompanies } from "../api/crm.ts";

export function CompaniesPage() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listCompanies(token, { search: search || undefined, limit, offset: page * limit })
      .then((res) => { setCompanies(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load companies"))
      .finally(() => setLoading(false));
  }, [token, search, page]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Companies</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <input
          type="text"
          placeholder="Search by name or domain…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        {!loading && companies.length === 0 && (
          <p className="text-gray-500">No companies found.</p>
        )}

        <div className="space-y-2">
          {companies.map((c) => (
            <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-white">{c.name}</p>
                  {c.domain && <p className="text-sm text-gray-400">{c.domain}</p>}
                </div>
                <div className="flex gap-2 shrink-0 text-xs">
                  {c.industry && <span className="px-2 py-0.5 rounded bg-gray-800 text-gray-300">{c.industry}</span>}
                  {c.size && <span className="px-2 py-0.5 rounded bg-gray-800 text-gray-300">{c.size}</span>}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-600">{new Date(c.createdAt).toLocaleDateString()}</p>
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500 self-center">
              {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
